import { Button, Spinner } from '@heroui/react'
import { PlanResponseDto, PlanWithBillingDto } from '@qse/api'
import { AlertCircle, Crown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import PricingCard from './PricingCard'

type Plan = PlanWithBillingDto | PlanResponseDto

interface PricingCardsContainerProps {
  plans: Plan[]
  isLoading?: boolean
  error?: Error | null
  onRetry?: () => void
  onSelectPlan: (planId: string) => void
  loadingPlanId?: string | null
  formatPrice: (amount: number) => string
  onCancelSubscription?: (planId: string) => void
  onResumeSubscription?: (planId: string) => void
  onUpgradePlan?: (planId: string) => void
  onDowngradePlan?: (planId: string) => void
  hasPendingCancellation?: boolean
}

const PricingCardsContainer = ({
  plans,
  isLoading = false,
  error,
  onRetry,
  onSelectPlan,
  loadingPlanId,
  formatPrice,
  onCancelSubscription,
  onResumeSubscription,
  onUpgradePlan,
  onDowngradePlan,
  hasPendingCancellation = false,
}: PricingCardsContainerProps) => {
  const [maxHeight, setMaxHeight] = useState<number>(0)
  const [isCalculatingHeights, setIsCalculatingHeights] = useState(true)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const [cardsRendered, setCardsRendered] = useState(false)

  // Calculate heights after cards are rendered
  useEffect(() => {
    if (!cardsRendered || plans.length === 0) return

    const calculateHeights = () => {
      let max = 0

      cardRefs.current.forEach(cardRef => {
        if (cardRef) {
          // Get the natural height of the card
          const height = cardRef.offsetHeight

          max = Math.max(max, height)
        }
      })

      if (max > 0) {
        setMaxHeight(max)
        setIsCalculatingHeights(false)
      } else {
        // If heights are still 0, try again
        setTimeout(calculateHeights, 100)
      }
    }

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      setTimeout(calculateHeights, 100)
    })
  }, [cardsRendered, plans])

  // Reset when plans change
  useEffect(() => {
    if (plans.length > 0) {
      setIsCalculatingHeights(true)
      setMaxHeight(0)
      setCardsRendered(false)
    }
  }, [plans])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100dvh-100px)]">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <Spinner color="primary" size="lg" />
          </div>
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
            Loading Subscription Plans...
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Please wait while we fetch your subscription information
          </p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4 max-w-md mx-auto p-6">
          <div className="flex justify-center">
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Failed to Load Subscription Plans
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {error?.message || 'An error occurred while fetching subscription plans'}
          </p>
          <div className="flex gap-3 justify-center">
            <Button className="mt-4" color="primary" onPress={onRetry}>
              Retry
            </Button>
            <Button className="mt-4" color="secondary" onPress={() => window.location.reload()}>
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="mx-auto w-24 h-24 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
            <Crown className="w-12 h-12 text-gray-500 dark:text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Subscription Plans Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
            There are currently no subscription plans configured. Please contact your administrator
            for assistance.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-8">
      <div className="text-center mb-4">
        <h2 className="text-3xl font-semibold text-gray-900 dark:text-white mb-4">
          Available Plans
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          Compare your current plan with other available options
        </p>
      </div>

      {/* Render cards with height calculation */}
      <div className="relative mx-auto flex w-fit flex-wrap justify-center gap-[30px]">
        {plans.map((plan, index) => {
          // Skip plans without billing product data
          if (!plan.billingProduct) return null
          if (!plan.isActive && !plan.isCurrentPlan) return null

          const monthlyPrice = formatPrice(plan.billingProduct.defaultPrice.amount)
          const isCurrentPlan = plan.isCurrentPlan || false
          const isFree = monthlyPrice === '0'

          // Only determine action type if we're in subscription management mode (handlers provided)
          // For billing page, we want to use the default "Select Plan" button
          const isSubscriptionManagementMode =
            !!onCancelSubscription || !!onResumeSubscription || !!onUpgradePlan || !!onDowngradePlan

          let actionType: 'cancel' | 'resume' | 'upgrade' | 'downgrade' | null = null
          let actionLabel: string | undefined
          let onAction: (() => void) | undefined

          // Only set action buttons in subscription management mode
          if (isSubscriptionManagementMode && !isFree) {
            if (isCurrentPlan) {
              // Current paid plan - show cancel or resume
              if (hasPendingCancellation) {
                actionType = 'resume'
                actionLabel = 'Resume Subscription'
                onAction = onResumeSubscription ? () => onResumeSubscription(plan.id) : undefined
              } else {
                actionType = 'cancel'
                actionLabel = 'Cancel Subscription'
                onAction = onCancelSubscription ? () => onCancelSubscription(plan.id) : undefined
              }
            } else if (plan.isSelectable) {
              // Upgrade
              actionType = 'upgrade'
              actionLabel = 'Upgrade'
              onAction = onUpgradePlan ? () => onUpgradePlan(plan.id) : undefined
            } else {
              // Downgrade
              actionType = 'downgrade'
              actionLabel = 'Downgrade'
              onAction = onDowngradePlan ? () => onDowngradePlan(plan.id) : undefined
            }
          }

          return (
            <div
              key={plan.id}
              ref={el => {
                cardRefs.current[index] = el
                // Mark as rendered when all cards have refs
                if (index === plans.length - 1 && cardRefs.current.every(ref => ref)) {
                  setCardsRendered(true)
                }
              }}
            >
              <PricingCard
                actionLabel={actionLabel}
                actionType={actionType}
                currency={plan.billingProduct.defaultPrice.currency}
                description={plan.billingProduct.description?.replace(/<[^>]*>/g, '') || plan.name}
                features={plan.billingProduct.features || []}
                hoverCard={false}
                interval={plan.billingProduct.defaultPrice.interval}
                isActionLoading={loadingPlanId === plan.id}
                isCurrentPlan={isCurrentPlan}
                isLoading={loadingPlanId === plan.id}
                isSubscriptionManagementMode={isSubscriptionManagementMode}
                label={plan.billingProduct.name}
                minHeight={isCalculatingHeights ? 'auto' : `${maxHeight}px`}
                monthlyPrice={monthlyPrice}
                showCurrentPlanBadge={true}
                isDisabled={
                  (loadingPlanId !== null && loadingPlanId !== plan.id) ||
                  (!actionType && !plan.isSelectable && !isCurrentPlan)
                }
                onAction={onAction}
                onSelectPlan={() => onSelectPlan(plan.id)}
              />
            </div>
          )
        })}

        {/* Show loading overlay while calculating heights */}
        {isCalculatingHeights && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-black/80 backdrop-blur-sm rounded-lg">
            <div className="text-center space-y-4">
              <Spinner color="primary" size="lg" />
              <p className="text-gray-600 dark:text-gray-400">Preparing cards...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default PricingCardsContainer
